import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save, Trash2, FileEdit, Plus, Search } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg, img, tl } from "@/lib/api";
import { PageTitle } from "@/pages/admin/AdminLayout";
import { EMPTY, COLS, ProductRowInputs, toPayload } from "@/pages/admin/productRow";
import ProductDetailEditor from "@/pages/admin/ProductDetailEditor";

const Head = ({ extra = [] }) => (
  <thead className="bg-[#172554] text-white text-xs">
    <tr>{["", ...COLS, ...extra].map((c, i) => <th key={i} className="px-2 py-2 text-left font-semibold whitespace-nowrap">{c}</th>)}</tr>
  </thead>
);

function QuickAdd({ onDone }) {
  const [rows, setRows] = useState(() => Array.from({ length: 6 }, () => ({ ...EMPTY })));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const filled = rows.filter((r) => r.name.trim());
    if (!filled.length) return toast.error("En az bir satıra ürün adı yazın");
    if (filled.some((r) => r.price === "" || Number(r.price) <= 0)) return toast.error("Doldurulan satırlarda fiyat girin");
    setBusy(true);
    try {
      await api.post("/admin/products/bulk", filled.map(toPayload));
      toast.success(`${filled.length} ürün eklendi`);
      setRows(Array.from({ length: 6 }, () => ({ ...EMPTY })));
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="bg-white border rounded-lg mb-8" data-testid="quick-add-section">
      <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b">
        <div>
          <h2 className="font-display text-lg font-bold text-[#172554]">Hızlı Ürün Ekle (6 satır)</h2>
          <p className="text-xs text-slate-500">Her satır bir ürün. Boş bıraktığınız satırlar kaydedilmez. Resim ve özellikleri kaydettikten sonra "Detay" ile ekleyin.</p>
        </div>
        <button data-testid="quick-add-save-button" disabled={busy} onClick={save} className="h-10 px-5 rounded-md bg-[#16A34A] text-white font-bold flex items-center gap-2 disabled:opacity-60">
          <Plus className="h-4 w-4" /> Hepsini Kaydet
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <Head />
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="px-2 text-xs text-slate-400 font-bold">{i + 1}</td>
                <ProductRowInputs v={r} prefix={`quick-row-${i}`} set={(nv) => setRows(rows.map((x, j) => (j === i ? nv : x)))} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ExistingRow({ p, onSaved, onDetail, onDelete }) {
  const [v, setV] = useState(p);
  useEffect(() => setV(p), [p]);
  const dirty = JSON.stringify(v) !== JSON.stringify(p);
  const save = async () => {
    try {
      await api.put(`/admin/products/${p.id}`, toPayload(v));
      toast.success("Kaydedildi");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <tr className={`border-b ${v.stock < 10 ? "bg-amber-50/60" : ""} ${!v.active ? "opacity-60" : ""}`} data-testid={`product-row-${p.id}`}>
      <td className="p-1 min-w-[52px]"><button data-testid={`row-thumb-${p.id}`} onClick={() => onDetail(p)}><img src={img(v.image)} alt="" className="h-10 w-10 object-contain border rounded bg-white" /></button></td>
      <ProductRowInputs v={v} set={setV} prefix={`row-${p.id}`} />
      <td className="p-1 whitespace-nowrap text-xs">
        <div className="font-bold text-[#15803D]">{tl(p.sale_price)}</div>
        {p.bulk_price && <div className="text-slate-500">Toplu: {tl(p.bulk_price)}</div>}
      </td>
      <td className="p-1 text-xs text-center">{p.sold_count || 0}</td>
      <td className="p-1">
        <div className="flex gap-1">
          <button data-testid={`row-save-${p.id}`} disabled={!dirty} onClick={save} title="Kaydet" className="h-9 w-9 rounded bg-[#16A34A] text-white flex items-center justify-center disabled:bg-slate-200 disabled:text-slate-400"><Save className="h-4 w-4" /></button>
          <button data-testid={`row-detail-${p.id}`} onClick={() => onDetail(p)} title="Detay / Resim" className="h-9 px-2 rounded bg-[#172554] text-white flex items-center gap-1 text-xs font-semibold"><FileEdit className="h-4 w-4" />Detay</button>
          <button data-testid={`row-delete-${p.id}`} onClick={() => onDelete(p)} title="Kaldır" className="h-9 w-9 rounded border border-red-200 text-red-600 flex items-center justify-center hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
        </div>
      </td>
    </tr>
  );
}

export default function AdminProducts() {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-products"], queryFn: () => api.get("/admin/products").then((r) => r.data) });
  const [detail, setDetail] = useState(null);
  const [del, setDel] = useState(null);
  const [q, setQ] = useState("");
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-products"] });
    qc.invalidateQueries({ queryKey: ["products"] });
  };
  const doDelete = async () => {
    await api.delete(`/admin/products/${del.id}`);
    toast.success("Ürün kaldırıldı");
    setDel(null);
    refresh();
  };
  const list = data.filter((p) => !q || `${p.name} ${p.brand} ${p.flavor}`.toLocaleLowerCase("tr").includes(q.toLocaleLowerCase("tr")));

  return (
    <>
      <PageTitle title="Ürün Yönetimi" desc="Ürün ekle, kampanyaya al, fiyat / iskonto / toplu alım indirimi ve kargo ayarını tek satırda düzenle." />
      <QuickAdd onDone={refresh} />
      <section className="bg-white border rounded-lg">
        <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b">
          <h2 className="font-display text-lg font-bold text-[#172554]">Ürünlerim ({data.length})</h2>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
            <input data-testid="admin-product-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün ara" className="h-9 pl-8 pr-3 border rounded-md text-sm" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-products-table">
            <Head extra={["Satış fiyatı", "Satılan", "İşlem"]} />
            <tbody>
              {list.map((p) => <ExistingRow key={p.id} p={p} onSaved={refresh} onDetail={setDetail} onDelete={setDel} />)}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500 p-4">"Kargo bizden" seçilen ürünlerde gizli kargo tutarı müşteriye gösterilmeden satış fiyatına eklenir ve ürün "Ücretsiz Kargo" etiketiyle görünür. Sarı satırlar stoğu 10'un altındaki ürünlerdir.</p>
      </section>
      <ProductDetailEditor product={detail} onClose={() => setDetail(null)} onSaved={refresh} />
      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ürün kaldırılsın mı?</AlertDialogTitle>
            <AlertDialogDescription>{del?.name} kalıcı olarak silinecek. Sadece gizlemek için "Detay"dan yayından kaldırabilirsiniz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm" onClick={doDelete} className="bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
